import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import appointmentService from '../../../../services/appointment.service';
import Pagination from '../../../common/Pagination';
import SurveyFollowupsSkeleton from './SurveyFollowupsSkeleton';
import {
    SURVEY_CHANNELS,
    buildDefaultChannelSelection,
    formatAppointmentDateTime,
    formatPatientName,
    getChannelState,
    getRowChannelSelection,
    getRowKey,
    getSendableRowKeys,
    isRowSendable,
    resolvePreferredChannel,
} from './surveyFollowupChannel';
import {
    buildSurveyFollowupsPrintSubtitle,
    printSurveyFollowupsList,
} from './surveyFollowupsPrint';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function SurveyFollowupsPanel({ refreshToken = 0, onCountUpdate }) {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [channelSelections, setChannelSelections] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
    const selectAllRef = useRef(null);

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const fetchRecords = useCallback(
        async (page, size, options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const res = await appointmentService.getSurveyFollowups(clinicId, page, size);
                const data = res.data;
                const content = data.content || [];

                setRecords(content);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
                setChannelSelections(
                    content.reduce((acc, record) => {
                        acc[getRowKey(record)] = buildDefaultChannelSelection(record);
                        return acc;
                    }, {})
                );
                setSelectedRowKeys(new Set(getSendableRowKeys(content)));
                onCountUpdate?.(data.totalElements ?? 0);
            } catch (err) {
                console.error('Failed to load survey followups', err);
                toast.error('Failed to load survey followups');
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId, onCountUpdate]
    );

    useEffect(() => {
        fetchRecords(pageNumber, pageSize);
    }, [fetchRecords, pageNumber, pageSize]);

    useEffect(() => {
        if (!refreshToken) {
            return;
        }

        const refresh = async () => {
            setIsRefreshing(true);
            try {
                await fetchRecords(pageNumber, pageSize, { silent: true });
            } finally {
                setIsRefreshing(false);
            }
        };

        refresh();
    }, [refreshToken, fetchRecords, pageNumber, pageSize]);

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(0);
    };

    const handleChannelSelect = (rowKey, channelKey, disabled) => {
        if (disabled) {
            return;
        }

        setChannelSelections((prev) => ({
            ...prev,
            [rowKey]: channelKey,
        }));
    };

    const sendableRowKeys = useMemo(() => getSendableRowKeys(records), [records]);

    const allSendableSelected =
        sendableRowKeys.length > 0 && sendableRowKeys.every((rowKey) => selectedRowKeys.has(rowKey));

    const someSendableSelected = sendableRowKeys.some((rowKey) => selectedRowKeys.has(rowKey));

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSendableSelected && !allSendableSelected;
        }
    }, [someSendableSelected, allSendableSelected]);

    const handleToggleSelectAll = () => {
        if (allSendableSelected) {
            setSelectedRowKeys(new Set());
            return;
        }

        setSelectedRowKeys(new Set(sendableRowKeys));
    };

    const handleToggleRow = (rowKey, record) => {
        if (!isRowSendable(record)) {
            return;
        }

        setSelectedRowKeys((prev) => {
            const next = new Set(prev);

            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }

            return next;
        });
    };

    const toolbarMeta = useMemo(
        () => ({
            pageLabel: `Page ${totalPages === 0 ? 0 : pageNumber + 1} of ${Math.max(totalPages, 1)}`,
            totalLabel: `${totalElements} patient${totalElements === 1 ? '' : 's'}`,
        }),
        [pageNumber, totalPages, totalElements]
    );

    const handlePrint = () => {
        const printed = printSurveyFollowupsList({
            records,
            channelSelections,
            title: 'Patient Engagement — Surveys',
            subtitle: buildSurveyFollowupsPrintSubtitle({
                recordsCount: records.length,
                totalElements,
                pageNumber,
                totalPages,
            }),
        });

        if (!printed) {
            toast.error('Unable to open print preview. Please try again.');
        }
    };

    return (
        <div className="d-flex flex-column h-100">
            <div className="ecx-page-panel__header">
                <div className="thank-you-surveys-toolbar">
                    <div className="thank-you-surveys-toolbar__meta">
                        <h5 className="mb-0 fw-semibold text-dark">Surveys</h5>
                        <span className="ecx-page-chip">
                            <i className="bi bi-list-ul"></i>
                            {toolbarMeta.totalLabel}
                        </span>
                        <span className="ecx-page-chip">
                            <i className="bi bi-layers"></i>
                            {toolbarMeta.pageLabel}
                        </span>
                    </div>

                    <div className="thank-you-surveys-toolbar__controls">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handlePrint}
                            disabled={loading || records.length === 0}
                        >
                            <i className="bi bi-printer me-1"></i>
                            Print
                        </button>

                        <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                            Show
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '5rem' }}
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                disabled={loading}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                            entries
                        </label>
                    </div>
                </div>
            </div>

            <div className="ecx-page-panel__body d-flex flex-column p-0">
                {loading ? (
                    <SurveyFollowupsSkeleton count={pageSize > 8 ? 8 : pageSize} />
                ) : records.length > 0 ? (
                    <>
                        <div className="table-responsive thank-you-surveys-table-wrap">
                            <table className="table table-hover align-middle mb-0 thank-you-surveys-table">
                                <thead>
                                    <tr>
                                        <th className="thank-you-surveys-select-col ps-3">
                                            <input
                                                ref={selectAllRef}
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={allSendableSelected}
                                                disabled={sendableRowKeys.length === 0 || loading}
                                                onChange={handleToggleSelectAll}
                                                aria-label="Select all patients on this page"
                                            />
                                        </th>
                                        <th>Patient</th>
                                        <th>Email</th>
                                        <th>Cell Phone</th>
                                        <th>Home Phone</th>
                                        <th>Appointment</th>
                                        {SURVEY_CHANNELS.map((channel) => (
                                            <th key={channel.key} className="text-center">
                                                {channel.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((record) => {
                                        const rowKey = getRowKey(record);
                                        const patientName = formatPatientName(record);
                                        const preferredChannel = resolvePreferredChannel(record);
                                        const selection = getRowChannelSelection(channelSelections, rowKey, record);
                                        const rowSendable = isRowSendable(record);
                                        const rowSelected = selectedRowKeys.has(rowKey);

                                        return (
                                            <tr key={rowKey} className={rowSelected ? 'thank-you-surveys-row--selected' : ''}>
                                                <td className="thank-you-surveys-select-col ps-3">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={rowSelected}
                                                        disabled={!rowSendable || loading}
                                                        onChange={() => handleToggleRow(rowKey, record)}
                                                        aria-label={`Select ${patientName}`}
                                                    />
                                                </td>
                                                <td className="thank-you-surveys-patient">
                                                    <Link
                                                        to={`/patient-files/${record.id}`}
                                                        className="thank-you-surveys-patient-link fw-semibold"
                                                    >
                                                        {patientName}
                                                    </Link>
                                                </td>
                                                <td className="thank-you-surveys-contact">
                                                    {record.email || <span className="text-muted">—</span>}
                                                </td>
                                                <td className="thank-you-surveys-contact">
                                                    {record.cellPhone || <span className="text-muted">—</span>}
                                                </td>
                                                <td className="thank-you-surveys-contact">
                                                    {record.homePhone || <span className="text-muted">—</span>}
                                                </td>
                                                <td className="thank-you-surveys-appointment">
                                                    {formatAppointmentDateTime(record)}
                                                </td>
                                                {SURVEY_CHANNELS.map((channel) => {
                                                    const state = getChannelState(record, channel.key);
                                                    const isPreferred = preferredChannel === channel.key;
                                                    const checked = selection === channel.key;
                                                    const title = state.disabled
                                                        ? record.isSurvey === false
                                                            ? 'Survey reminders disabled in communication preferences'
                                                            : !state.available
                                                              ? 'No email contact on file'
                                                              : 'Email disabled in communication preferences'
                                                        : isPreferred
                                                          ? 'Preferred email channel'
                                                          : channel.label;

                                                    return (
                                                        <td key={`${rowKey}-${channel.key}`} className="text-center">
                                                            <div
                                                                className={`thank-you-surveys-channel ${
                                                                    isPreferred ? 'is-preferred' : ''
                                                                } ${state.disabled ? 'is-disabled' : ''}`}
                                                                title={title}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name={`survey-channel-${rowKey}`}
                                                                    className="form-check-input"
                                                                    checked={checked}
                                                                    disabled={state.disabled || loading}
                                                                    onChange={() =>
                                                                        handleChannelSelect(rowKey, channel.key, state.disabled)
                                                                    }
                                                                    aria-label={`${channel.label} for ${patientName}`}
                                                                />
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="ecx-page-pagination border-top p-3 mt-auto">
                                <Pagination
                                    currentPage={pageNumber}
                                    totalPages={totalPages}
                                    totalElements={totalElements}
                                    pageSize={pageSize}
                                    onPageChange={setPageNumber}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="ecx-page-empty flex-grow-1 thank-you-surveys-panel--surveys-empty">
                        <div className="ecx-page-empty__icon">
                            <i className="bi bi-clipboard-check"></i>
                        </div>
                        <h5 className="text-dark fw-semibold mb-2">No survey followups</h5>
                        <p className="mb-0">
                            There are no confirmed appointments from the last 7 days.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SurveyFollowupsPanel;
